import React from 'react';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import Alert from 'Components/DesignSystem/Alert/Alert';
import Tag from 'Components/DesignSystem/Tag/Tag';
import { TISRProject } from './DefineModel/model';

type BulkDeleteModalProps = {
    open: boolean;
    selectedModels: TISRProject[];
    onClose: () => void;
    onConfirm: () => Promise<void>;
    loading?: boolean;
    /**
     * The current user's id. Used purely for the preventive, in-modal preview of
     * which selected models would be skipped for ownership reasons — the server
     * still runs the authoritative owner check and returns `not_owner_count` /
     * `skipped_reasons.not_owner` on the response.
     */
    currentUserId?: string;
};

const BulkDeleteModal = ({ open, selectedModels, onClose, onConfirm, loading = false, currentUserId }: BulkDeleteModalProps) => {
    const guardedClose = () => { if (!loading) onClose(); };

    // Only the model owner can bulk-delete; non-owned selections will be skipped
    // server-side. Preview that here so the user sees it before confirming,
    // instead of only reading it in the toast after the request. If we don't
    // have a current user (defensive), we don't block anything — the server
    // still runs the check.
    const nonOwnedModels = currentUserId
        ? selectedModels.filter(m => m?.createdBy !== currentUserId)
        : [];
    const allNonOwned = selectedModels.length > 0 && nonOwnedModels.length === selectedModels.length;

    return (
        <Dialog
            open={open}
            onClose={guardedClose}
            title="Delete Model(s)"
            tone="danger"
            size="md"
            data-testid="bulk-delete-modal"
            footer={
                <div className="tw-flex tw-w-full tw-gap-2 tw-justify-end">
                    <Button
                        variant="secondary"
                        disabled={loading}
                        onClick={guardedClose}
                        data-testid="bulk-delete-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        loading={loading}
                        /* If every selected model is non-owned, there's nothing to
                           delete — disable confirm rather than fire a request that
                           would return a 0/N toast. */
                        disabled={loading || allNonOwned}
                        onClick={onConfirm}
                        data-testid="bulk-delete-confirm-btn"
                    >
                        Delete Model(s)
                    </Button>
                </div>
            }
        >
            <div className="tw-flex tw-flex-col tw-gap-4">
                <Alert tone="danger" data-testid="bulk-delete-undone-callout">
                    This action cannot be undone.
                </Alert>
                {nonOwnedModels.length > 0 && (
                    <Alert tone="warning" data-testid="bulk-delete-owner-warning">
                        {allNonOwned
                            ? 'Only the model owner has delete permission. None of the selected models will be deleted.'
                            : `Only the model owner has delete permission. ${nonOwnedModels.length} of ${selectedModels.length} selected model(s) will be skipped.`}
                    </Alert>
                )}
                <div>
                    <p className="tw-text-sm tw-font-medium tw-text-ink tw-mb-2">
                        Selected Model(s) ({selectedModels.length})
                    </p>
                    <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-max-h-28 tw-overflow-y-auto" data-testid="bulk-delete-chips">
                        {selectedModels.map((m) => (
                            <Tag
                                key={m.id}
                                label={m.ISRName}
                                variant="meta"
                                title={m.ISRName}
                                data-testid={`bulk-delete-chip-${m.id}`}
                            />
                        ))}
                    </div>
                </div>
                <p className="tw-text-sm tw-text-ink-secondary">
                    All UI Models, rules, and configuration under these Guide Models will be permanently removed.
                </p>
            </div>
        </Dialog>
    );
};

export default BulkDeleteModal;
